import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { GET_BASE_URL } from '../constants/apiEndpoints';

/**
 * Shared @user autocomplete for the Lounge composers.
 *
 * The composers used to fire an un-debounced `/api/v1/getUser` request on every
 * keystroke and render whichever response landed last. This hook debounces the
 * lookup, encodes the query, caches results per search term for the session,
 * and drops stale responses so a slow early request cannot replace a newer one.
 */

export const MENTION_SUGGESTION_DEBOUNCE_MS = 250;

/** normalized search term -> filtered dropdown items */
const suggestionCache = new Map<string, any[]>();
/** normalized search term -> in-flight search, so duplicates share one request */
const inFlightSearches = new Map<string, Promise<any[]>>();

export function fetchMentionSuggestions(searchTerm: string): Promise<any[]> {
  const key = searchTerm.toLowerCase();

  if (suggestionCache.has(key)) {
    return Promise.resolve(suggestionCache.get(key) as any[]);
  }

  const pending = inFlightSearches.get(key);
  if (pending) {
    return pending;
  }

  const request = axios
    .get(GET_BASE_URL + '/api/v1/getUser?name=' + encodeURIComponent(searchTerm))
    .then((response: any) => {
      const results = Array.isArray(response?.data?.data) ? response.data.data : [];
      // Preserve the existing dropdown item shape and client-side filter.
      const includesSearchTerm = results.filter((item: any) =>
        String(item?.value ?? '').toLowerCase().includes(key)
      );

      suggestionCache.set(key, includesSearchTerm);
      return includesSearchTerm;
    })
    .catch(() => [] as any[])
    .finally(() => {
      inFlightSearches.delete(key);
    });

  inFlightSearches.set(key, request);
  return request;
}

type MentionSuggestionsOptions = {
  debounceMs?: number;
};

export function useMentionSuggestions(options?: MentionSuggestionsOptions) {
  const debounceMs = options?.debounceMs ?? MENTION_SUGGESTION_DEBOUNCE_MS;

  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [searchText, setSearchText] = useState('');

  const selectedMentionRef = useRef('');
  const selectionTimerRef = useRef<any>(null);
  const debounceTimerRef = useRef<any>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const cancelPendingSearch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    // Any response still in flight belongs to an older request now.
    requestIdRef.current += 1;
  }, []);

  const clearSuggestions = useCallback(() => {
    cancelPendingSearch();
    setSuggestions([]);
  }, [cancelPendingSearch]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (selectionTimerRef.current) {
        clearTimeout(selectionTimerRef.current);
      }
    };
  }, []);

  /**
   * Drop-in replacement for the Quill mention `source` callback.
   */
  const mentionSource = useCallback(
    (searchTerm: any, renderList: any, mentionChar: any, _callback?: any) => {
      setSearchText(searchTerm);
      cancelPendingSearch();
      // The visible dropdown is our own list; Quill's built-in list stays empty.
      renderList?.([]);

      const normalizedSearchTerm = String(searchTerm ?? '').toLowerCase();

      if (
        selectedMentionRef.current &&
        normalizedSearchTerm === selectedMentionRef.current
      ) {
        selectedMentionRef.current = '';
        setSuggestions([]);
        return;
      }

      if (mentionChar !== '@' || !searchTerm || searchTerm.length === 0) {
        setSuggestions([]);
        return;
      }

      const requestId = requestIdRef.current;

      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;

        fetchMentionSuggestions(searchTerm).then((results: any[]) => {
          // Last request wins: ignore anything superseded by newer typing.
          if (!mountedRef.current || requestId !== requestIdRef.current) {
            return;
          }

          setSuggestions(results);
          renderList?.([]);
        });
      }, debounceMs);
    },
    [cancelPendingSearch, debounceMs]
  );

  /**
   * Suppresses the follow-up `source` call Quill fires right after a mention is
   * inserted, so the dropdown does not immediately reopen on the new text.
   */
  const markMentionSelected = useCallback((username: string) => {
    const normalizedUsername = String(username ?? '').toLowerCase();

    if (!normalizedUsername) {
      return;
    }

    selectedMentionRef.current = normalizedUsername;

    if (selectionTimerRef.current) {
      clearTimeout(selectionTimerRef.current);
    }

    selectionTimerRef.current = setTimeout(() => {
      selectionTimerRef.current = null;
      if (selectedMentionRef.current === normalizedUsername) {
        selectedMentionRef.current = '';
      }
    }, 1000);
  }, []);

  return {
    suggestions,
    searchText,
    setSearchText,
    mentionSource,
    clearSuggestions,
    markMentionSelected,
  };
}

/** Test-only helper: drops the session cache between test cases. */
export function resetMentionSuggestionCache(): void {
  suggestionCache.clear();
  inFlightSearches.clear();
}
